import { usePreviewCreate, usePreviewGet } from "@/lib/api/preview";
import { AreaSchema } from "@/lib/types/area";
import { convertScannerSchema, Scanner, scannerSchema, ScannerSchema } from "@/lib/types/scanner";
import { formatDate, getUuid } from "@/lib/utils";
import { ActionIcon, Button, Group, NumberInput, Stack, TextInput } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import { FaCheck, FaPencil, FaPlus, FaRotateRight, FaTrash, FaXmark } from "react-icons/fa6";
import ReactCrop, { Crop } from "react-image-crop";
import { LoadingSpinner } from "../molecules/LoadingSpinner";
import { LoadableImage } from "../atoms/LoadableImage";
import { CardGray } from "../atoms/CardGray";

type Props = {
  scanner?: Scanner;
  onSubmit: (scanner: ScannerSchema) => void;
  close: () => void;
}

export const ScannerForm = ({ scanner, onSubmit, close }: Props) => {
  const { data: preview, isLoading: isLoadingPreview } = usePreviewGet(scanner ?? { id: 0 })
  const isGeneratingImage = preview && !preview.hasFile
  const imgURL = preview && preview.hasFile ? `/api/preview/scan/${preview.id}` : undefined

  const form = useForm<ScannerSchema>({
    initialValues: scanner ? convertScannerSchema(scanner) : {
      name: "",
      areas: [],
    },
    validate: zod4Resolver(scannerSchema)
  });

  const previewCreate = usePreviewCreate()

  const handleUpdateImage = () => {
    previewCreate.mutate(scanner ?? { id: 0 }, {
      onSuccess: () => notifications.show({ variant: "success", title: "New image requested", message: "This can take a while (>5 minutes). You can safely exit this menu in the meantime" })
    })
  }

  const handleAddArea = () => {
    form.insertListItem("areas", {
      iid: getUuid(),
      interval: "",
      width: 0,
      height: 0,
      offsetX: 0,
      offsetY: 0,
    })
  }

  const handleDeleteArea = (idx: number) => {
    form.removeListItem("areas", idx)
  }

  const handleSubmit = async () => {
    if (form.validate().hasErrors) return

    onSubmit(form.values)
  }

  if (!preview && !isLoadingPreview) {
    return (
      <div>
        <p>{`This scanner doesn't have a preview image yet`}</p>
        <p>{`Let's generate one!`}</p>
        <Button onClick={handleUpdateImage} variant="outline" loading={isLoadingPreview}>
          Update image
        </Button>
      </div>
    )
  }

  return (
    <Stack>
      <TextInput
        label="Name"
        placeholder="Pi 2"
        {...form.getInputProps("name")}
      />
      <Stack>
        <Group gap="xs">
          <span>{`Areas (${form.values.areas.length})`}</span>
          <ActionIcon onClick={handleAddArea} color="black" variant="transparent" loading={isLoadingPreview} disabled={isGeneratingImage}>
            <FaPlus />
          </ActionIcon>
        </Group>
        <Button onClick={handleUpdateImage} variant="outline" leftSection={<FaRotateRight />} loading={isLoadingPreview} className="w-fit" disabled={isGeneratingImage}>
          Update image
        </Button>
        {preview && !isGeneratingImage && (
          <span className="text-muted text-sm">{`Image date: ${formatDate(preview.createdAt)}`}</span>
        )}
        {!isGeneratingImage && imgURL ? (
          <Stack gap="xs">
            {form.values.areas.map((a, idx) => (
              <AreaForm key={`${a.iid}`} idx={idx} form={form} imgURL={imgURL} onDelete={handleDeleteArea} />
            ))}
          </Stack>
        ) : (
          <div>
            <p>The image is still generating</p>
            <p>Come back in a couple of minutes</p>
            <LoadingSpinner className="pt-4" />
          </div>
        )}
      </Stack>
      <Group justify="end">
        <Button onClick={close} variant="outline">
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoadingPreview} disabled={isGeneratingImage}>
          Submit
        </Button>
      </Group>
    </Stack>
  )
}

const convertAreaToCrop = (area: AreaSchema): Crop => {
  return {
    unit: "%",
    x: area.offsetX * 100,
    y: area.offsetY * 100,
    width: area.width * 100,
    height: area.height * 100,
  }
}

const convertCropToArea = (area: AreaSchema, crop: Crop): AreaSchema => {
  return {
    id: area.id,
    iid: area.iid,
    offsetX: crop.x / 100,
    offsetY: crop.y / 100,
    width: crop.width / 100,
    height: crop.height / 100,
    intervalS: area.intervalS,
  }
}

type AreaProps = {
  idx: number;
  form: UseFormReturnType<ScannerSchema>;
  imgURL: string;
  onDelete: (idx: number) => void;
}

const AreaForm = ({ idx, form, imgURL, onDelete }: AreaProps) => {
  const [drawing, setDrawing] = useState(false)
  const [crop, setCrop] = useState<Crop>(convertAreaToCrop(form.values.areas[idx]))

  const handleStartDrawing = () => {
    setDrawing(true)
  }

  const handleCancelDrawing = () => {
    setDrawing(false)
    setCrop(convertAreaToCrop(form.values.areas[idx]))
  }

  const handleSubmitDrawing = () => {
    setDrawing(false)

    const newArea = convertCropToArea(form.values.areas[idx], crop)

    form.setFieldValue(`areas.${idx}`, newArea)
  }

  const error = form.errors["areas.3.width"] || form.errors["areas.3.height"] || form.errors["areas.3.offsetX"] || form.errors["areas.3.offsetY"] ? "Invalid area" : ""

  return (
    <CardGray className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="font-semibold">{`Area ${idx + 1}`}</span>
        <ActionIcon onClick={() => onDelete(idx)} color="red" variant="transparent">
          <FaTrash />
        </ActionIcon>
      </div>
      <NumberInput
        label="Scan interval (in seconds)"
        placeholder="3600"
        {...form.getInputProps(`areas.${idx}.intervalS`)}
      />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span>Location</span>
          {drawing ? (
            <>
              <ActionIcon onClick={handleCancelDrawing} color="red" variant="transparent">
                <FaXmark />
              </ActionIcon>
              <ActionIcon onClick={handleSubmitDrawing} color="green" variant="transparent">
                <FaCheck />
              </ActionIcon>
            </>
          ) : (
            <ActionIcon onClick={handleStartDrawing} color="black" variant="transparent">
              <FaPencil />
            </ActionIcon>
          )}
        </div>
        {error && <span className="text-sm text-red-400">{error}</span>}
        <span className="text-muted text-sm">Press the pencil icon to draw a new area</span>
      </div>
      <div>
        {imgURL ? (
          <ReactCrop crop={crop} onChange={(_, percentage) => setCrop(percentage)} disabled={!drawing} className="border border-orange-499">
            <LoadableImage src={imgURL} />
          </ReactCrop>
        ) : (
          <LoadingSpinner />
        )}
      </div>
    </CardGray>
  )
}
